import { Avatar } from "../components/Avatar";
import { ConversationMessageContent } from "../components/ConversationMessages";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, } from "../components/Dropdown";
import { IconButton } from "../components/IconButton";
import { DotsHorizontal } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
/** Centered, capped-width column that lays out ConversationMessage turns with responsive gaps. */
export const ConversationContainer = React.forwardRef(({ children, className, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("flex h-full w-full flex-col items-center @container/conversation", className), ...props },
        React.createElement("div", { className: "flex w-full max-w-4xl flex-col gap-6 p-2 @sm-conversation:gap-8 @md-conversation:gap-10" }, children)));
});
ConversationContainer.displayName = "ConversationContainer";
const messageVariants = cva("flex w-full flex-col rounded-2xl", {
    variants: {
        type: {
            user: "bg-muted-background px-5 py-4 gap-2",
            agent: "w-full gap-3",
        },
    },
    defaultVariants: {
        type: "agent",
    },
});
const buttonsVariants = cva("flex justify-start gap-2 pt-2", {
    variants: {
        type: {
            user: "justify-end",
            agent: "justify-start",
        },
    },
    defaultVariants: {
        type: "agent",
    },
});
/**
 * Parent component for both UserMessage and AgentMessage, to ensure avatar,
 * side buttons and spacing are consistent between the two.
 * @deprecated Use ConversationMessages instead.
 * @summary Legacy single chat message wrapper.
 */
export const ConversationMessage = React.forwardRef(({ avatarBusy = false, buttons, children, citations, isDisabled = false, name, timestamp, completionStatus, pictureUrl, renderName = (name) => React.createElement("span", null, name), infoChip, type, actions, className, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: "group/message" },
        React.createElement("div", { className: cn(messageVariants({ type, className })), ...props },
            React.createElement(ConversationMessageHeader, { avatarUrl: pictureUrl, name: name, timestamp: timestamp, completionStatus: completionStatus, isBusy: avatarBusy, isDisabled: isDisabled, renderName: renderName, infoChip: infoChip, actions: actions }),
            React.createElement(ConversationMessageContent, { citations: citations, type: type }, children)),
        buttons && (React.createElement("div", { className: cn(buttonsVariants({ type, className })) }, buttons))));
});
ConversationMessage.displayName = "ConversationMessage";
const ConversationMessageHeader = React.forwardRef(({ avatarUrl, isBusy, isDisabled, name = "", timestamp, infoChip, completionStatus, renderName, actions, className, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("flex items-center gap-2 p-1 @sm-conversation:p-0", className), ...props },
        React.createElement(Avatar, { className: "@sm:hidden", name: name, visual: avatarUrl, busy: isBusy, disabled: isDisabled, size: "xs" }),
        React.createElement(Avatar, { className: "hidden @sm:flex", name: name, visual: avatarUrl, busy: isBusy, disabled: isDisabled, size: "sm" }),
        React.createElement("div", { className: "inline-flex w-full justify-between gap-0.5" },
            React.createElement("div", { className: "inline-flex items-baseline gap-2 text-foreground" },
                React.createElement("span", { className: "heading-sm" }, renderName(name)),
                React.createElement("span", { className: "text-xs text-muted-foreground" }, timestamp),
                infoChip && infoChip),
            React.createElement("div", { className: "flex items-center gap-2" }, completionStatus !== null && completionStatus !== void 0 ? completionStatus : null,
                actions && actions.length > 0 && (React.createElement(DropdownMenu, null,
                    React.createElement(DropdownMenuTrigger, { asChild: true },
                        React.createElement(IconButton, { icon: DotsHorizontal, size: "xs", variant: "highlight-ghost", "aria-label": "Message actions" })),
                    React.createElement(DropdownMenuContent, null, actions.map((action, index) => (React.createElement(DropdownMenuItem, { key: index, icon: action.icon, label: action.label, onClick: action.onClick }))))))))));
});
ConversationMessageHeader.displayName = "ConversationMessageHeader";
//# sourceMappingURL=ConversationMessage.js.map